import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { api, money, shortDate, riskColor } from "../lib/api";
import { PageHeader, Panel, NeonButton } from "../components/common";
import { ConsentModal } from "../components/consent/ConsentModal";

const inp = "bg-field border border-fieldline rounded-sm px-3 py-2 text-sm w-full focus:outline-none focus:border-brand";

const Row = ({ k, v, testid, cls = "" }) => (
  <div><dt className="label-xs">{k}</dt><dd className={`break-all ${cls}`} data-testid={testid}>{v}</dd></div>
);

const Receipt = ({ r, onCancelled }) => {
  const c = r.consent;
  const [cancelled, setCancelled] = useState(false);
  const cancel = async () => {
    try {
      await api.post(`/consent/${c.id}/cancel`);
      setCancelled(true);
      toast.success("Cancelled within the 30-minute window — payment voided");
      onCancelled();
    } catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };
  return (
    <div className="mt-6 p-4 border border-brand/30 rise" data-testid="consent-receipt">
      <p className="flex items-center gap-2 font-mono text-xs text-brand"><ShieldCheck className="w-4 h-4" /> CONSENT SEALED · {c.layers.length} LAYERS · ECDSA P-256</p>
      <p className="text-xs text-white/60 mt-2" data-testid="consent-narrative">
        {c.typed_name} signed on {new Date(c.created_at).toUTCString()} (server time) from IP {c.ip || "n/a"}, device {c.device_hash.slice(0, 10)}, {c.device?.user_agent?.split(") ")[0]?.split("(")[1] || "browser"}. Consent text hash {c.terms_hash.slice(0, 12)}.
      </p>
      <dl className="mt-3 space-y-2 font-mono text-[11px]">
        <Row k="consent record sha-256" v={c.hash} testid="consent-hash" cls="text-white/70" />
        <Row k="consent text hash" v={c.terms_hash} testid="consent-terms-hash" cls="text-white/60" />
        <Row k="device fingerprint hash" v={c.device_hash} testid="consent-device-hash" cls="text-white/60" />
        <Row k="rfc 3161 timestamp" v={c.tsa?.status === "granted" ? `${c.tsa.gen_time} · ${c.tsa.serial} · freetsa.org` : `not granted (${c.tsa?.error || "unknown"})`} testid="consent-tsa" cls={c.tsa?.status === "granted" ? "text-accent2" : "text-[#F87171]"} />
        <Row k="transaction · risk" v={<>{r.transaction.id} · <span style={{ color: riskColor(r.transaction.risk_score) }}>{r.transaction.risk_score}</span></>} />
        <Row k="cancel window until" v={new Date(c.cancel_until).toLocaleTimeString()} />
      </dl>
      <p className="font-mono text-[10px] text-[#FFB800] mt-3">Post-payment confirmation email is MOCKED (logged as evidence, not sent).</p>
      <NeonButton variant="danger" className="mt-3" disabled={cancelled} onClick={cancel} data-testid="consent-cancel-btn">{cancelled ? "Cancelled" : "Cancel within 30 min"}</NeonButton>
    </div>
  );
};

const ConsentRow = ({ c }) => {
  const [v, setV] = useState(null);
  const check = () => api.get(`/consent/${c.id}`).then((r) => setV(r.data.verification));
  return (
    <li className="flex items-center justify-between py-3 border-b border-divider" data-testid={`consent-row-${c.id}`}>
      <div>
        <p className="text-sm">{c.customer_name} {c.schema === "v2" && <span className="font-mono text-[9px] text-brand ml-1">V2 · {c.layers?.length} LAYERS</span>}{c.cancelled_at && <span className="font-mono text-[9px] text-[#F87171] ml-1">CANCELLED</span>}</p>
        <p className="font-mono text-[10px] text-white/35">{shortDate(c.created_at)} · {c.hash.slice(0, 16)}… {c.ip ? `· ${c.ip}` : ""}</p>
      </div>
      <button onClick={check} data-testid={`verify-consent-${c.id}`}
        className={`font-mono text-[10px] uppercase text-right ${v === null ? "text-white/45 hover:text-brand" : v.valid ? "text-brand" : "text-[#F87171]"}`}>
        {v === null ? "verify" : v.valid ? `valid${v.ecdsa_valid ? " · ecdsa" : ""}${v.tsa_valid ? " · tsa" : ""}` : "tampered"}
      </button>
    </li>
  );
};

export default function ConsentCapture() {
  const [form, setForm] = useState({ customer_name: "Jordan Reyes", customer_email: "jordan.reyes@mail.com", product_name: "Pro Analytics Annual", amount: 499 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [list, setList] = useState([]);
  const load = () => api.get("/consent").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const confirm = async (signed) => {
    setBusy(true);
    try {
      const { data } = await api.post("/consent", { ...form, amount: Number(form.amount), ...signed });
      setReceipt(data);
      setOpen(false);
      toast.success("Payment authorized with signed consent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Consent capture failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="consent-capture-page">
      <PageHeader eyebrow="// consent capture & proof" title="Checkout Shield" />
      <div className="grid grid-cols-12 gap-6">
        <Panel title="Demo checkout" className="col-span-12 lg:col-span-6">
          <div className="space-y-4">
            {[["customer_name", "Cardholder name"], ["customer_email", "Email"], ["product_name", "Product"], ["amount", "Amount (USD)"]].map(([k, l]) => (
              <div key={k}><p className="label-xs mb-1">{l}</p>
                <input className={inp} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`checkout-${k}`} /></div>
            ))}
            <NeonButton onClick={() => setOpen(true)} className="w-full justify-center !py-3" data-testid="checkout-pay-btn"><Lock className="w-3.5 h-3.5" /> Pay {money(form.amount)}</NeonButton>
          </div>
          {receipt && <Receipt key={receipt.consent.id} r={receipt} onCancelled={load} />}
        </Panel>
        <Panel title="Consent ledger" className="col-span-12 lg:col-span-6">
          <ul className="max-h-[520px] overflow-y-auto pr-2" data-testid="consent-list">{list.map((c) => <ConsentRow key={c.id} c={c} />)}</ul>
        </Panel>
      </div>
      <ConsentModal key={String(open)} open={open} onOpenChange={setOpen} onConfirm={confirm} busy={busy} amount={form.amount} email={form.customer_email} name={form.customer_name} />
    </div>
  );
}
